import type { MediaStatus, MediaVariantDto } from '@/common/media';

export enum WS_EVENT_TYPES {
  SUBSCRIBE = 'subscribe',

  // COMMUNICATION
  NEW_MESSAGE = 'new_message',
  EDIT_MESSAGE = 'edit_message',
  DELETE_MESSAGE = 'delete_message',
  WRITING_MESSAGE = 'writing_message',
  MESSAGES_READ = 'messages_read',
  READ_MESSAGE = 'read_message',
  ASSIGN_USER = 'assign_user',
  UNASSIGN_USER = 'unassign_user',
  SUBSCRIBE_NOTIFICATION = 'subscribe_notification',
  MEDIA_FILE_UPDATED = 'media_file_updated',

  // VESSEL
  DELETE_VESSEL = 'delete_vessel',

  // ACTIVITY
  UPCOMING_EVENT = 'upcoming_event',
  TASK_REMINDER = 'task_reminder',
  ADDED_AS_PARTICIPANT_TASK = 'added_as_participant_task',
  ADDED_AS_SUPERVISOR = 'added_as_supervisor',
  ADDED_AS_RESPONSIBLE = 'added_as_responsible',
  ADDED_AS_PARTICIPANT_EVENT = 'added_as_participant_event',
  TASK_UPDATED = 'task_updated',
  EVENT_UPDATED = 'event_updated',
  TASK_DELETED = 'task_deleted',

  // PEOPLE
  ROLES_ASSIGNED = 'roles_assigned',

  // CONNECTION
  PING = 'ping',
  PONG = 'pong',
}

export type WSEventType = (typeof WS_EVENT_TYPES)[keyof typeof WS_EVENT_TYPES];

// Base WebSocket Message Structure
export type WSMessage<T = unknown> = {
  type: WSEventType;
  data: T;
};

// Message Model
export type WSMessageModel = {
  id: string;
  text: string;
  created_at: string;
  updated_at?: string;
  creator_id: string;
  room_id: string;
  quote_message_id?: string;
  mentions?: Record<string, string>;
  media_files: WSMediaFile[];
};

export type WSMediaFile = {
  id: string;
  file_name: string;
  preview_file_name?: string;
  name: string;
  type: 'image' | 'video' | 'audio' | 'file';
  url?: string;
  link?: string;
  size?: number;
  /** Video and audio only — see {@link MediaStatus}. */
  media_status?: MediaStatus | null;
  /** Unlinked: each one is the file's link + `/variants/<name>`. */
  variants?: Record<string, MediaVariantDto>;
};

/**
 * The encoder is done with a chat file, one way or the other. A ready one has
 * been renamed to its new extension and changed type and size, so those come
 * along; the room's muted members are not sent it.
 */
export type WSMediaFileUpdated = {
  id: string;
  message_id: string;
  room_id: string;
  media_status: MediaStatus;
  file_name: string;
  /** The content type, as `MediaFile.type`. */
  type: string;
  size: number;
  variants?: Record<string, MediaVariantDto>;
};

// User Model
export type WSUser = {
  id: string;
  name: string;
  role: string;
};

// Request/Response Types

// 1. Subscribe
export type SubscribeRequest = WSMessage<{
  room_id: string;
}>;

export type SubscribeResponse = WSMessage<string>;

// 2. New Message
export type NewMessageRequest = WSMessage<{
  text: string;
  tid: string; // temporary id for frontend
  room_id?: string;
  quote_message_id?: string;
  mentions?: Record<string, string>;
}>;

export type NewMessageResponse = WSMessage<{
  message: WSMessageModel;
  tid: string;
}>;

// 3. Edit Message
export type EditMessageRequest = WSMessage<{
  message_id: string;
  text: string;
  quote_message_id?: string;
}>;

export type EditMessageResponse = WSMessage<{
  message: WSMessageModel;
}>;

// 4. Delete Message
export type DeleteMessageRequest = WSMessage<{
  message_id: string;
}>;

export type DeleteMessageResponse = WSMessage<{
  message: {
    id: string;
  };
}>;

// 4b. Messages Read
export type MessagesReadResponse = WSMessage<{
  room_id: string;
  message_ids: string[];
  user_id?: string;
}>;

// 5. Writing Message
export type WritingMessageRequest = WSMessage<Record<string, never>>;

export type WritingMessageResponse = WSMessage<{
  user: WSUser;
  room_id: string;
}>;

// 6. Assign User
export type AssignUserResponse = WSMessage<string[]>;

// 7. Unassign User
export type UnassignUserResponse = WSMessage<string[]>;

// 8. Subscribe Notification
export type SubscribeNotificationRequest = WSMessage<Record<string, never>>;

export type SubscribeNotificationResponse = WSMessage<Record<string, never>>;

export type UpcomingEventResponse = WSMessage<{
  id: string;
  title: string;
  type: string;
  start: string;
  end: string;
}> & {
  type: typeof WS_EVENT_TYPES.UPCOMING_EVENT;
};

export type AddedAsParticipantEventResponse = WSMessage<{
  id: string;
  title: string;
}> & {
  type: typeof WS_EVENT_TYPES.ADDED_AS_PARTICIPANT_EVENT;
};

export type EventUpdatedResponse = WSMessage<{
  id: string;
  title: string;
}> & {
  type: typeof WS_EVENT_TYPES.EVENT_UPDATED;
};

export type TaskReminderResponse = WSMessage<{
  id: string;
  title: string;
  type: string;
  end: string;
}> & {
  type: typeof WS_EVENT_TYPES.TASK_REMINDER;
};

export type AddedAsParticipantTaskResponse = WSMessage<{
  id: string;
  title: string;
}> & {
  type: typeof WS_EVENT_TYPES.ADDED_AS_PARTICIPANT_TASK;
};

export type AddedAsSupervisorResponse = WSMessage<{
  id: string;
  title: string;
}> & {
  type: typeof WS_EVENT_TYPES.ADDED_AS_SUPERVISOR;
};

export type AddedAsResponsibleResponse = WSMessage<{
  id: string;
  title: string;
}> & {
  type: typeof WS_EVENT_TYPES.ADDED_AS_RESPONSIBLE;
};

export type TaskUpdatedResponse = WSMessage<{
  id: string;
  title: string;
}> & {
  type: typeof WS_EVENT_TYPES.TASK_UPDATED;
};

export type TaskDeletedResponse = WSMessage<{
  id: string;
  title: string;
}> & {
  type: typeof WS_EVENT_TYPES.TASK_DELETED;
};

export type DeleteVesselResponse = WSMessage<{
  vessel_id: string;
  vessel_name: string;
}> & {
  type: typeof WS_EVENT_TYPES.DELETE_VESSEL;
};

export type RolesAssignedResponse = WSMessage<{
  roles: string[];
  service_id: string;
}> & {
  type: typeof WS_EVENT_TYPES.ROLES_ASSIGNED;
};

// Connection Status
export type WSConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

// Typing Indicator
export type TypingUser = {
  userId: string;
  userName: string;
  roomId: string;
};
